import { Component, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, NavigationCancel } from '@angular/router';
import { Session } from 'app/shared/services/session/session.service';
import { ApplicationService } from 'app/shared/services/app/application/application.service';
import { AnalyticsService } from 'app/shared/services/analytics/analytics.service';
import { RoleType } from 'app/shared/services/user/user-application-properties.model';
import { ActiveUser } from '../../services/user/active-user.model';
import { GlobalRole } from '../../services/user/base-user.model';

@Component({
  selector: 'tog-left-nav',
  templateUrl: 'left-nav.template.html',
  styleUrls: ['left-nav.scss'],
  providers: [ApplicationService]
})
export class LeftNavComponent implements OnDestroy {
  features: any[];
  currentApp: string = null;
  routerBlock: boolean = false;
  includeUsers: boolean = true;
  user: ActiveUser;
  url: string;
  subscriptions: any[] = [];

  constructor(private session: Session,
              private _router: Router,
              private _applicationService: ApplicationService,
              private _analytics: AnalyticsService) {
    this.session.user().then((user) => {
      this.includeUsers = user.globalRole.level >= GlobalRole.SuperAdmin;
      this.user = user;
      if (this.url) {
        let route = this.handleUrl(this.url);
        this.features.forEach((feature) => {
          if (feature.route === route) {
            feature.active = true;
          } else feature.active = false;
          if (feature.name === 'Users' && route.indexOf('/students') != -1) {
            feature.active = true;
          }
        });
      }
    });
    // Watch for changes in the route and update the application as well as the features
    // on the left appropriately
    this.subscriptions.push(this._router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd && !this.routerBlock) {
        if (!this.user) {
          this.url = e.url;
        }
        else {
          this.user = this.session.cachedUser; // to catch any in-memory updates that have been made
          let route = this.handleUrl(e.url);
          this.features.forEach((feature) => {
            if (feature.route === route) {
              feature.active = true;
            } else feature.active = false;
            if (feature.name === 'Users' && route.indexOf('/students') != -1) {
              feature.active = true;
            }
            this.checkFeatureIndicatorStatus(feature);
          });
        }
      } else if (e instanceof NavigationCancel) {
        this.preventRouting();
      }
    }));
  }

  // Prevents routing for user cancel screens. This is because NavigationEnd
  // stupidly comes after NavigationCancel.
  preventRouting() {
    let self = this;
    this.routerBlock = true;
    setTimeout(() => {
      self.routerBlock = false;
    }, 50);
  }

  navigate(feature: any) {
    this._router.navigateByUrl(feature.route);
  }

  buildFeature(name: string, icon: string, route: string, tooltip: string): any {
    return { name: name, icon: icon, route: route, active: false, tooltip: tooltip, newIndicator: false };
  }

  setDashboardFeatures() {
    this._analytics.clearAppAnalytics();
    this.currentApp = null;
    if (this.includeUsers) {
      this.features = [
        this.buildFeature('Dashboard', 'oi-bar-chart', '/dashboard', 'Status update, help, and more'),
        this.buildFeature('Users', 'oi-person', '/users', 'Manage and see accounts'),
        this.buildFeature('Account Settings', 'oi-cog', '/account_settings', 'Update universal and technical settings')
      ];
      this.features[0].newIndicator = false; // no indicator ever on the admin dashboard
    }
    else {
      this.features = [
        this.buildFeature('Account Settings', 'oi-cog', '/account_settings', 'Update universal and technical settings')
      ];
    }
  }

  checkFeatureIndicatorStatus(feature: any) {
    if (feature.name.toLowerCase() == 'dashboard') {
      feature.newIndicator = this.session.newestArticleOnDashboard > this.user.lastViewedDashboard &&
                              this.user.globalRole.level != GlobalRole.SuperAdmin;
    }
  }

  handleUrl(url: string): string {
    let splitUrl = url.substr(1).split('/');
    if (splitUrl[0] === 'app' && splitUrl[1]) {
      if (splitUrl[1] !== this.currentApp) {
        this.setAppFeatures(splitUrl[1]);
        let appUrl = '/app/' + splitUrl[1];
        if (splitUrl[2]) appUrl += '/' + splitUrl[2];
        return appUrl;
      }
    } else {
      this.setDashboardFeatures();
      return url;
    }
  }

  setAppFeatures(sysName: string) {
    let localUserRole = this.user.getLocalUserRole(this.session.selectedAppSysName);
    if (this.user.globalRole.level > GlobalRole.Client) {
      this.features = [
        this.buildFeature('Dashboard', 'oi-bar-chart', '/app/' + sysName, 'Status update, help, and more'),
        this.buildFeature('Features', 'oi-list-rich', '/app/' + sysName + '/features', 'Create the mobile app'),
        this.buildFeature('Users', 'oi-person', '/app/' + sysName + '/users', 'Manage and see accounts'),
        this.buildFeature('App Settings', 'oi-cog', '/app/' + sysName + '/settings', 'Update universal and technical settings')
      ];
    } else if (localUserRole == RoleType.Owner) {
      this.features = [
        this.buildFeature('Dashboard', 'oi-bar-chart', '/app/' + sysName, 'Status update, help, and more'),
        this.buildFeature('Features', 'oi-list-rich', '/app/' + sysName + '/features', 'Create the mobile app'),
        this.buildFeature('Users', 'oi-person', '/app/' + sysName + '/users', 'Manage and see accounts')
      ];
    } else {
      this.features = [
        this.buildFeature('Features', 'oi-list-rich', '/app/' + sysName + '/features', 'Create the mobile app')
      ];
    }
    this.features.forEach((feature) => {
      this.checkFeatureIndicatorStatus(feature);
    });
  };

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/left-nav/left-nav.component.ts